import React from 'react';
import styled from 'styled-components';

import { RED, YELLOW } from 'constants/colours';
import { GuestStatus } from 'types/guest.types';
import { EditableSession } from 'types/event.types';
import dayjs from 'utils/extendedDayJS';
import { FULL_DATE_FORMAT } from 'constants/dates';

const CancelledStatusText = styled.span`
  color: ${RED};
`;

const IncompleteStatusText = styled.span`
  color: ${YELLOW};
`;

/**
 * Map of label to display value
 */
const labelMappings: Record<string, string> = {
  Drafts: 'Draft'
};

/**
 * Transform label to display value
 * @param label
 * @returns
 */
export const transformLabel = (label: string): string => {
  return labelMappings[label] || label;
};

export const formatPlural = (value: number | boolean | undefined): string => {
  if (value === undefined) {
    return '';
  }

  if (typeof value === 'boolean') {
    return `${value ? 's' : ''}`;
  }
  return `${value > 1 ? 's' : ''}`;
};
/**
 * Return comma separated number.
 * eg:
 * 1000 -> 1,000
 * @param number
 * @returns
 */
export const formatNumberWithComma = (number: number): string => {
  return number.toLocaleString('en-US');
};

/**
 * Return number with number of decimals
 * eg: 2 decimals
 * 1000 -> 1,000.00
 * 1000.1234 -> 1,000.12
 * 1000.123456 -> 1,000.12
 * @param num
 * @param decimals
 * @returns
 */
export const formatNumberWithDecimals = (num: number, decimals = 1): string => {
  return (Math.round(num * 100) / 100).toFixed(decimals);
};

/**
 * eg:
 * 0 => 0 members
 * 1 => 1 member
 * 4000 => 4,000 members
 * @param memberCount
 * @returns
 */
export const formatDistrolistMember = (memberCount: number): string => {
  return `${formatNumberWithComma(memberCount)} member${
    memberCount > 1 || memberCount === 0 ? 's' : ''
  }`;
};

/**
 * Format Guest status to display value
 * Used in insights page
 * @param status
 * @returns
 */
export const formatStatusText = (status: GuestStatus): React.ReactElement | string => {
  if (status.toLowerCase() === GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY) {
    return <IncompleteStatusText>Incomplete</IncompleteStatusText>;
  }
  if (status.toLocaleLowerCase() === GuestStatus.CANCELLED) {
    return <CancelledStatusText>Not going</CancelledStatusText>;
  }
  if (status.toLocaleLowerCase() === GuestStatus.ACTION_REQUIRED) {
    return <span>Pending</span>;
  }
  if (status.toLocaleLowerCase() === GuestStatus.INTERESTED) {
    return <span>Maybe</span>;
  }
  if (status.toLocaleLowerCase() === GuestStatus.NONE) {
    return <span>-</span>;
  }
  if (status.toLocaleLowerCase() === GuestStatus.CONFIRMED) {
    return <span>Going</span>;
  }

  // Capitalize the first letter of the status
  return <span>{status[0].toUpperCase() + status.slice(1).toLocaleLowerCase()}</span>;
};

export const formatEventSessionPayload = (session: EditableSession): any => {
  // Strip maxRegisteredPlusOnes from plusOneConfig as it should not be sent to the backend
  let plusOneConfigForPayload = session?.plusOneConfig;
  if (plusOneConfigForPayload) {
    const { maxRegisteredPlusOnes: _omit, ...cleanConfig } = plusOneConfigForPayload;
    plusOneConfigForPayload = cleanConfig;
  }

  return {
    id: session?.id,
    title: session?.title,
    description: session?.description,
    datetimeScheduled: session?.datetimeScheduled
      ? dayjs(session.datetimeScheduled).format(FULL_DATE_FORMAT)
      : null,
    datetimeEnd: session?.datetimeEnd ? dayjs(session.datetimeEnd).format(FULL_DATE_FORMAT) : null,
    allDay: session?.allDay,
    isInPerson: session?.isInPerson,
    companyLocationId: session?.companyLocationId,
    physicalLocation: session?.physicalLocation,
    isVirtual: session?.isVirtual,
    virtualLocation: session?.virtualLocation,
    zoomMeetingId: session?.zoomMeetingId,
    googleMeetingId: session?.googleMeetingId,
    maxAttendees: session?.maxAttendees,
    targetAttendance: session?.targetAttendance,
    hasUnlimitedSpots: session?.hasUnlimitedSpots,
    plusOneConfig: plusOneConfigForPayload
  };
};

/**
 * Formats a timezone code into a human-readable display string.
 *
 * This function uses the Intl.DateTimeFormat API to format the timezone name
 * according to the specified format. If no timezone code is provided, it defaults
 * to the user's local timezone. If formatting fails, it returns the raw timezone code.
 *
 * @param {string} [timezoneCode] - The IANA timezone code (e.g., "America/New_York").
 *   If not provided, the user's local timezone will be used.
 * @param {Intl.DateTimeFormatOptions['timeZoneName']} [format='long'] - The display format for the timezone name.
 *   Accepts 'long', 'short', 'shortOffset', 'longOffset', 'shortGeneric', or 'longGeneric'.
 *   Defaults to 'long'.
 * @returns {string} The formatted timezone display string, or the raw timezone code if formatting fails.
 *
 * @example
 * formatTimezoneDisplay('America/Los_Angeles', 'short'); // "PDT"
 * formatTimezoneDisplay('Europe/London', 'long'); // "Greenwich Mean Time"
 * formatTimezoneDisplay(); // Uses local timezone, e.g., "Eastern Daylight Time"
 */
export const formatTimezoneDisplay = (
  timezoneCode?: string,
  format: Intl.DateTimeFormatOptions['timeZoneName'] = 'long'
): string => {
  const tzCode = timezoneCode || Intl.DateTimeFormat().resolvedOptions().timeZone; // Default to local timezone

  try {
    return (
      new Intl.DateTimeFormat(undefined, { timeZone: tzCode, timeZoneName: format })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')?.value || tzCode
    );
  } catch (error) {
    console.error('Error formatting timezone display:', error);
    return tzCode;
  }
};
